import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import { CacheManager } from '@/data/hook/query/v2/CacheFacade';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import CoursePath from '@/models/CoursePath';
import { CheckCircleIcon } from '@heroicons/react/16/solid';
import ContPedStatus from './PedContentStatus';
import useContPedStatus, { ValidationError } from './useContPedStatus';
import ValidationErrorHandler from './validation/ValidationErrorHandler';

type ContPedStatusButtonProps = {
  status: VersioningStatusEnum;
  coursePath: CoursePath;
  cache: CacheManager<CoursePath>;
  version: number;
};

export default function ContPedStatusButton({
  status,
  coursePath,
  cache,
  version,
}: ContPedStatusButtonProps) {
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  const onInvalidate = (error: ValidationError) => {
    setValidationError(error);
  };

  const { validateAndPublish, isPending } = useContPedStatus({
    coursePath,
    cache,
    onInvalidate,
  });

  const { t: tCourseLineForm } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseWorkspace',
  });

  const isVersioning = status === VersioningStatusEnum.VERSIONING;

  return (
    <div className="flex items-center gap-4">
      <ContPedStatus status={status} version={version} />
      <ConditionalRenderer condition={isVersioning}>
        <Button
          color="primary"
          onClick={() => validateAndPublish()}
          disabled={isPending}
          loading={isPending}
        >
          <CheckCircleIcon className="w-3.5 h-3.5" />
          {tCourseLineForm('publish')}
        </Button>
      </ConditionalRenderer>
      <ValidationErrorHandler
        error={validationError}
        onClose={() => setValidationError(null)}
      />
    </div>
  );
}
